import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ROLE_VARIANTS } from '../../../lib/roles';
import { accountGroups, greeting } from './accountLinks';
import { AccountLinkList, CountBadge, HEADER_PANEL, HEADER_TEXT, accountRowClass } from './headerParts';
import useDismiss from './useDismiss';
import { focusables, onPanelKeyDown } from './menuKeys';

/**
 * The desktop (800px+) account area of the site header, between the nav and "Get started".
 * Signed out: a quiet "Log in" text control (opens the AuthModal; its "Sign up" switch is
 * inside). Signed in: "Hi, {first name}" with the active-codes badge, a disclosure button
 * that opens a panel under the bar: the dashboards the HEADER role variant grants, Deal
 * wallet (badge), My profile, then Log out.
 *
 * Keyboard: Enter/Space toggles; ArrowDown on the button opens and focuses the first item;
 * ArrowUp/ArrowDown/Home/End move within the panel; Escape closes and returns focus to the
 * button; Tab past the last item, or a click outside, closes it.
 *
 * While the session is still being checked (`loading`) it renders an invisible, inert
 * placeholder the width of "Log in", so a signed-in visitor never sees "Log in" flash.
 *
 * Props: `user`, `isAuthenticated`, `loading`, `activeClaims`, `pathname`, `open` /
 * `onOpenChange` (state lives in SiteHeader so a navigation closes it), `onLogin`, `onLogout`.
 */
export default function AccountMenu({
  user, isAuthenticated, loading = false, activeClaims, pathname, open, onOpenChange, onLogin, onLogout,
}) {
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = `site-account-${useId().replace(/:/g, '')}`;
  const [focusFirst, setFocusFirst] = useState(false);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  useDismiss(open, close, wrapRef, buttonRef);

  useEffect(() => {
    if (!open || !focusFirst) return;
    setFocusFirst(false);
    const [first] = focusables(panelRef.current);
    if (first) first.focus();
  }, [open, focusFirst]);

  if (loading) {
    return (
      <span
        aria-hidden="true"
        data-account-placeholder=""
        className={cn(HEADER_TEXT, 'invisible hidden shrink-0 select-none [@media(min-width:50em)]:inline-block')}
      >
        Log in
      </span>
    );
  }

  if (!isAuthenticated) {
    return (
      <button
        type="button"
        onClick={onLogin}
        className={cn(HEADER_TEXT, 'hidden shrink-0 cursor-pointer border-0 px-0 [@media(min-width:50em)]:inline-block')}
      >
        Log in
      </button>
    );
  }

  const onButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusFirst(true);
      onOpenChange(true);
    }
  };

  const groups = accountGroups(user, { variant: ROLE_VARIANTS.HEADER, activeClaims });

  return (
    <div ref={wrapRef} className="hidden shrink-0 items-center [@media(min-width:50em)]:flex">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onOpenChange(!open)}
        onKeyDown={onButtonKeyDown}
        className={cn(HEADER_TEXT, 'inline-flex cursor-pointer items-center gap-1.5 border-0 px-0', open && 'underline')}
      >
        {/* 64px up to 1023px keeps the bar within 760px at 800px wide, badge included */}
        <span className="max-w-[4rem] truncate [@media(min-width:64em)]:max-w-[10rem]">{greeting(user)}</span>
        <CountBadge count={activeClaims} />
        <ChevronDown
          aria-hidden="true"
          size={14}
          strokeWidth={1.5}
          className={cn('shrink-0 motion-safe:transition-transform motion-safe:duration-180', open && 'rotate-180')}
        />
      </button>
      {/* Positioned against the bar (the fixed <header>), right-aligned under its end. */}
      <div
        id={panelId}
        ref={panelRef}
        hidden={!open}
        onKeyDown={onPanelKeyDown}
        className={cn(HEADER_PANEL, 'absolute right-[-1px] top-full w-[17.5rem] whitespace-normal px-5 py-4')}
      >
        <AccountLinkList groups={groups} pathname={pathname} onNavigate={close} size="sm" />
        <div className="mt-3 border-t border-brand-rule pt-3">
          <button type="button" onClick={onLogout} className={cn(accountRowClass('sm'), 'cursor-pointer border-0 px-0')}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
